// Library methods
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { HubConnection } from "@microsoft/signalr";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

// Types
import { ExamLensType } from "src/services/exams/types";

// Contexts
import { useLiveUpdateContext } from "src/contexts/live-update/LiveUpdateContext";

// Hooks
import useTokenAccess from "src/hooks/auth/useTokenAccess";

// Services
import { deviceUpdateLensParameter } from "src/services/signalR/api";

// Utils
import { isLensViewedByBothEyes } from "src/utils/exams/lensSimulationHelper";

type DryEyeSelectionCardType = {
  profile: ExamLensType;
  isDeviceReady: boolean;
  connection: HubConnection | null;
};

const DryEyeSelectionCard = ({
  profile,
  isDeviceReady,
  connection,
}: DryEyeSelectionCardType) => {
  const { t } = useTranslation();
  const { device } = useLiveUpdateContext();
  const { getToken } = useTokenAccess();

  const isSelected = isLensViewedByBothEyes(profile);

  const toggleView = useCallback(
    async (currentlyBeingViewed: boolean) => {
      if (!device?.id || !connection || !profile.name) return;

      try {
        const token = await getToken();
        await deviceUpdateLensParameter(
          token,
          device.id,
          {
            profileName: profile.name,
            currentlyBeingViewed,
          },
          connection
        );
      } catch (e) {
        console.error(e);
      }
    },
    [device?.id, connection, getToken, profile.name]
  );

  return (
    <Box
      onClick={() => {
        if (!isDeviceReady) return;
        toggleView(!isSelected);
      }}
      sx={{
        display: "flex",
        flexDirection: "column",
        p: "8px",
        border: isSelected ? "1px solid #E2772E" : "1px solid #5B6D7A40",
        borderRadius: "4px",
        minWidth: 0,
        cursor: isDeviceReady ? "pointer" : "default",
      }}
    >
      <Typography fontSize="14px" fontWeight={600} noWrap>
        {profile.name}
      </Typography>
      <Typography fontSize="12px" noWrap>
        {t(profile.severityType ?? "")}
      </Typography>
    </Box>
  );
};

export default DryEyeSelectionCard;
